/* MindBodyRecommender — Mind-Body Interventions widget.
 *
 * Features:
 *  1. Препоръки — top MBIs подредени по user's assigned phenotypes
 *     (GET /api/mindbody/recommendations).
 *  2. Session logger — записва practiced session (duration + оптионално
 *     perceived stress before/after) към mindbody_session_log.
 *  3. Weekly stats — last 7 days total minutes + pathway coverage
 *     (GET /api/mindbody/weekly-stats).
 *
 * Pathway Coverage контрибуция: всяка MBI покрива 3-5 KEGG pathway-и
 * (hsa04064 NF-κB, hsa04668 TNF, hsa04060 cytokines, hsa04024 cAMP,
 * hsa04722 BDNF) — директно смекчават chronic inflammation-а, който е
 * upstream драйвер на multiple phenotype conditions.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain, Sparkles, Play, ChevronDown, ChevronUp,
  CheckCircle2, X, Loader2, Timer, TrendingUp, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

const EVIDENCE_COLOR = {
  A: { dark: "bg-emerald-500/20 text-emerald-300", light: "bg-emerald-100 text-emerald-700" },
  B: { dark: "bg-amber-500/20 text-amber-300", light: "bg-amber-100 text-amber-700" },
  C: { dark: "bg-gray-500/20 text-gray-300", light: "bg-gray-100 text-gray-600" },
};

/**
 * i18n helper — взема `_bg` / `_en` / `_es` поле от dict-а според активния език,
 * с fallback верига EN → BG → key, за да не показваме никога празен низ.
 */
function pickLang(obj, base, lang) {
  if (!obj) return "";
  return obj[`${base}_${lang}`] || obj[`${base}_en`] || obj[`${base}_bg`] || "";
}

/** Намира етикет на phenotype key в текущия език — използва `targets_conditions_labels`
 *  ако е enrich-нато от backend-а; fallback — raw key.
 */
function pickConditionLabel(labelsArr, key, lang) {
  const rec = (labelsArr || []).find((l) => l.key === key);
  if (!rec) return key;
  return rec[lang] || rec.en || rec.bg || key;
}

function SessionLogModal({ open, onClose, intervention, API, isDark, onLogged, lang, bg }) {
  const [duration, setDuration] = useState(20);
  const [stressBefore, setStressBefore] = useState("");
  const [stressAfter, setStressAfter] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDuration(20);
      setStressBefore("");
      setStressAfter("");
      setNotes("");
    }
  }, [open]);

  if (!open || !intervention) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        intervention_id: intervention.id,
        duration_min: parseInt(duration) || 10,
      };
      if (stressBefore !== "") payload.perceived_stress_before = parseInt(stressBefore);
      if (stressAfter !== "") payload.perceived_stress_after = parseInt(stressAfter);
      if (notes.trim()) payload.notes = notes.trim();

      await axios.post(`${API}/mindbody/session-log`, payload);
      const label = pickLang(intervention, "name", lang);
      toast.success(
        bg ? `Записана сесия: ${label}`
        : lang === "es" ? `Sesión registrada: ${label}`
        : `Session logged: ${label}`
      );
      onLogged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || (bg ? "Грешка при запис"
            : lang === "es" ? "Error al guardar"
            : "Error saving session"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="mbi-session-modal"
    >
      <div
        className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden
          ${isDark ? "bg-gray-900 border border-white/10" : "bg-white"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <div>
            <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-600"}`}>
              {bg ? "Запиши сесия" : lang === "es" ? "Registrar sesión" : "Log session"}
            </div>
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {pickLang(intervention, "name", lang)}
            </h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded hover:bg-black/10 ${isDark ? "text-gray-400" : "text-gray-500"}`}
            data-testid="mbi-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className={`text-[11px] font-semibold mb-1 block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {bg ? "Продължителност (мин.) *"
               : lang === "es" ? "Duración (min) *"
               : "Duration (min) *"}
            </label>
            <input
              type="number"
              min="1"
              max="480"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border text-sm
                ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
              data-testid="mbi-duration-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-[11px] font-semibold mb-1 block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {bg ? "Стрес преди (0–10)"
                 : lang === "es" ? "Estrés antes (0–10)"
                 : "Stress before (0–10)"}
              </label>
              <input
                type="number"
                min="0" max="10"
                value={stressBefore}
                onChange={(e) => setStressBefore(e.target.value)}
                placeholder={bg ? "опц." : lang === "es" ? "opc." : "opt."}
                className={`w-full px-3 py-2 rounded-lg border text-sm
                  ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
                data-testid="mbi-stress-before"
              />
            </div>
            <div>
              <label className={`text-[11px] font-semibold mb-1 block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {bg ? "Стрес след (0–10)"
                 : lang === "es" ? "Estrés después (0–10)"
                 : "Stress after (0–10)"}
              </label>
              <input
                type="number"
                min="0" max="10"
                value={stressAfter}
                onChange={(e) => setStressAfter(e.target.value)}
                placeholder={bg ? "опц." : lang === "es" ? "opc." : "opt."}
                className={`w-full px-3 py-2 rounded-lg border text-sm
                  ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
                data-testid="mbi-stress-after"
              />
            </div>
          </div>

          <div>
            <label className={`text-[11px] font-semibold mb-1 block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {bg ? "Бележки (опц.)" : lang === "es" ? "Notas (opc.)" : "Notes (opt.)"}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={bg ? "Как се почувствахте..."
                : lang === "es" ? "Cómo se sintió..."
                : "How did you feel..."}
              className={`w-full px-3 py-2 rounded-lg border text-xs resize-none
                ${isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
              data-testid="mbi-notes-input"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving || !duration}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50
              ${isDark ? "bg-violet-500/30 text-violet-200 hover:bg-violet-500/40" : "bg-violet-600 text-white hover:bg-violet-700"}`}
            data-testid="mbi-save-session-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {bg ? "Запиши сесия" : lang === "es" ? "Guardar sesión" : "Save session"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InterventionCard({ mbi, isDark, onLog, onToggle, expanded, lang, bg }) {
  const ev = EVIDENCE_COLOR[mbi.evidence_level] || EVIDENCE_COLOR.C;
  const labelsArr = mbi.targets_conditions_labels || [];
  const allConditions = mbi.targets_conditions || [];
  const matchedSet = new Set(mbi.matched_conditions || []);
  return (
    <div
      className={`rounded-lg border ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}
      data-testid={`mbi-card-${mbi.id}`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-[9px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-600"}`}>
                {pickLang(mbi, "category", lang)}
              </span>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold
                ${isDark ? ev.dark : ev.light}`}>
                Evidence {mbi.evidence_level}
              </span>
              {mbi.match_count > 1 && (
                <span className={`text-[9px] ${isDark ? "text-indigo-300" : "text-indigo-600"}`}>
                  ✓ {mbi.match_count} {bg ? "фенотипа" : lang === "es" ? "fenotipos" : "phenotypes"}
                </span>
              )}
            </div>
            <div className={`text-sm font-bold mt-0.5 ${isDark ? "text-white" : "text-gray-900"}`}>
              {pickLang(mbi, "name", lang)}
            </div>
            <div className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {pickLang(mbi, "short_description", lang)}
            </div>

            {/* Phenotype chips — винаги видими, маркирани ако са част от
                user's assigned phenotypes. Дава яснота "за кого е полезна
                тази техника". */}
            {allConditions.length > 0 && (
              <div className="mt-2 flex items-start gap-1.5 flex-wrap" data-testid={`mbi-targets-${mbi.id}`}>
                <span className={`text-[9px] uppercase tracking-wider font-semibold mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? "За:" : lang === "es" ? "Para:" : "For:"}
                </span>
                {allConditions.map((k) => {
                  const active = matchedSet.has(k);
                  const label = pickConditionLabel(labelsArr, k, lang);
                  return (
                    <span
                      key={k}
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium
                        ${active
                          ? (isDark ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40"
                                    : "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-400/50")
                          : (isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-600")
                        }`}
                      title={active
                        ? (bg ? "Във вашите фенотипи" : lang === "es" ? "En tus fenotipos" : "In your phenotypes")
                        : label}
                      data-testid={`mbi-target-chip-${mbi.id}-${k}`}
                    >
                      {active && "✓ "}{label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <button
            onClick={() => onLog(mbi)}
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold transition-colors
              ${isDark ? "bg-violet-500/20 text-violet-300 hover:bg-violet-500/30" : "bg-violet-100 text-violet-700 hover:bg-violet-200"}`}
            data-testid={`mbi-log-btn-${mbi.id}`}
          >
            <Play className="w-3 h-3" />
            {bg ? "Запиши" : lang === "es" ? "Registrar" : "Log"}
          </button>
        </div>

        <button
          onClick={() => onToggle(mbi.id)}
          className={`mt-2 inline-flex items-center gap-1 text-[10px] ${isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
          data-testid={`mbi-toggle-${mbi.id}`}
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded
            ? (bg ? "скрий" : lang === "es" ? "ocultar" : "hide")
            : (bg ? "детайли" : lang === "es" ? "detalles" : "details")}
        </button>

        {expanded && (
          <div className={`mt-2 space-y-2 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
            data-testid={`mbi-details-${mbi.id}`}>
            {/* Phenotype-specific mechanisms */}
            {mbi.phenotype_mechanisms?.length > 0 && (
              <div className={`rounded p-2 ${isDark ? "bg-indigo-500/10" : "bg-indigo-50"}`}>
                <div className={`font-semibold mb-1 ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
                  {bg ? "⚕️ Защо помага за вашите фенотипи:"
                   : lang === "es" ? "⚕️ Por qué ayuda para tus fenotipos:"
                   : "⚕️ Why it helps for your phenotypes:"}
                </div>
                {mbi.phenotype_mechanisms.map((pm) => (
                  <div key={`${pm.condition}-${pm.pathway}`} className="mb-1.5 last:mb-0">
                    <div className={`font-semibold ${isDark ? "text-indigo-200" : "text-indigo-800"}`}>
                      {pickConditionLabel(labelsArr, pm.condition, lang)} · {pm.pathway}
                    </div>
                    <div className={`${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {pm[`mechanism_${lang}`] || pm.mechanism_en || pm.mechanism_bg}
                    </div>
                    {(pm[`evidence_${lang}`] || pm.evidence_en || pm.evidence_bg) && (
                      <div className={`italic ${isDark ? "text-gray-400" : "text-gray-500"} mt-0.5`}>
                        📊 {pm[`evidence_${lang}`] || pm.evidence_en || pm.evidence_bg}
                      </div>
                    )}
                    {pm.biomarker_target && (
                      <div className={`${isDark ? "text-emerald-300" : "text-emerald-700"} mt-0.5`}>
                        🎯 {pm.biomarker_target}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div>
              <span className="font-semibold">
                {bg ? "Дозировка:" : lang === "es" ? "Dosis:" : "Dose:"}
              </span>{" "}
              {pickLang(mbi, "dose", lang)}
            </div>
            <div>
              <span className="font-semibold">
                {bg ? "Общ ефект:" : lang === "es" ? "Efecto general:" : "Overall effect:"}
              </span>{" "}
              {pickLang(mbi, "effect_summary", lang)}
            </div>
            {mbi.biomarkers_affected?.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-semibold">
                  {bg ? "Биомаркери:" : lang === "es" ? "Biomarcadores:" : "Biomarkers:"}
                </span>
                {mbi.biomarkers_affected.map((b) => (
                  <span key={b} className={`px-1.5 py-0.5 rounded text-[9px]
                    ${isDark ? "bg-white/10" : "bg-gray-100"}`}>{b}</span>
                ))}
              </div>
            )}
            {mbi.targets_pathways?.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-semibold">
                  {bg ? "KEGG пътища:" : lang === "es" ? "Vías KEGG:" : "KEGG pathways:"}
                </span>
                {mbi.targets_pathways.map((p) => (
                  <a key={p} href={`https://www.kegg.jp/pathway/${p}`} target="_blank" rel="noopener noreferrer"
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono
                      ${isDark ? "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}>
                    {p}<ExternalLink className="w-2.5 h-2.5" />
                  </a>
                ))}
              </div>
            )}
            {mbi.citations?.length > 0 && (
              <div className={`${isDark ? "text-gray-500" : "text-gray-500"}`}>
                <span className="font-semibold">
                  {bg ? "Цитати:" : lang === "es" ? "Citas:" : "Citations:"}
                </span>
                <ul className="list-disc list-inside">
                  {mbi.citations.slice(0, 3).map((c) => (
                    <li key={String(c).slice(0, 48)} className="text-[10px]">{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {(pickLang(mbi, "contraindications", lang) || mbi.contraindications_bg) && (
              <div className={`rounded p-1.5 ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                {pickLang(mbi, "contraindications", lang) || mbi.contraindications_bg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MindBodyRecommender({ refreshKey = 0 }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : language === "es" ? "es" : "bg";
  const bg = lang === "bg";

  const [recs, setRecs] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [logModal, setLogModal] = useState({ open: false, intervention: null });
  const [showAll, setShowAll] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [recsRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/mindbody/recommendations`),
        axios.get(`${API}/mindbody/weekly-stats`),
      ]);
      setRecs(recsRes.data);
      setWeekly(weeklyRes.data);
    } catch (e) {
      // Silent — widget ще се скрие при no data
      setRecs(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  // Ако user няма assigned phenotype → показваме empty-state CTA вместо да скриваме widget-а.
  // Така потребителят вижда функционалността и следващата стъпка (phenotype classifier).
  if (!recs || !recs.recommendations?.length) {
    return (
      <div
        className={`rounded-lg overflow-hidden border ${isDark ? "border-violet-500/20 bg-violet-500/5" : "border-violet-200 bg-violet-50/40"}`}
        data-testid="mindbody-recommender-empty"
      >
        <div className="p-4 sm:p-5 flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDark ? "bg-violet-500/20" : "bg-violet-100"}`}>
            <Brain className={`w-5 h-5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
              Mind-Body Interventions
            </div>
            <div className={`text-sm font-bold mt-0.5 ${isDark ? "text-white" : "text-gray-900"}`}>
              {bg ? "Задайте фенотип за персонализирани препоръки"
               : lang === "es" ? "Configure su fenotipo para recomendaciones personalizadas"
               : "Set your phenotype for personalized recommendations"}
            </div>
            <div className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? "RCT-доказани психосоматични интервенции (медитация, йога, дишане, КПТ), насочени към Вашия здравен профил."
               : lang === "es" ? "Intervenciones psicosomáticas respaldadas por ECA (meditación, yoga, respiración, TCC), dirigidas a su perfil de salud específico."
               : "RCT-backed psychosomatic interventions (meditation, yoga, breathwork, CBT), targeted for your specific health profile."}
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('[data-testid="phenotype-classifier"]')
                  || document.querySelector('[id*="phenotype"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors
                ${isDark ? "bg-violet-500/30 text-violet-200 hover:bg-violet-500/40"
                         : "bg-violet-600 text-white hover:bg-violet-700"}`}
              data-testid="mbi-empty-cta"
            >
              {bg ? "Задай фенотип →"
               : lang === "es" ? "Configurar fenotipo →"
               : "Set phenotype →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const visible = showAll ? recs.recommendations : recs.recommendations.slice(0, 3);

  return (
    <>
      <div
        className={`rounded-lg overflow-hidden border ${isDark ? "border-violet-500/20 bg-violet-500/5" : "border-violet-200 bg-violet-50/40"}`}
        data-testid="mindbody-recommender"
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isDark ? "bg-violet-500/20" : "bg-violet-100"}`}>
              <Brain className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                Mind-Body Interventions
              </div>
              <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg
                  ? `${recs.recommendation_count} препоръки за ${recs.total_conditions} фенотип(а)`
                  : lang === "es"
                  ? `${recs.recommendation_count} recomendaciones para ${recs.total_conditions} fenotipo(s)`
                  : `${recs.recommendation_count} recommendations for ${recs.total_conditions} phenotype(s)`}
              </div>
            </div>
            {weekly && weekly.total_minutes > 0 && (
              <div className={`text-right ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                <div className="text-[9px] uppercase tracking-wider opacity-70">
                  {bg ? "7 дни" : lang === "es" ? "7 días" : "7 days"}
                </div>
                <div className="text-sm font-bold flex items-center gap-1 justify-end">
                  <Timer className="w-3 h-3" />
                  {weekly.total_minutes} min
                </div>
              </div>
            )}
          </div>

          {/* Explainer — what is MBI & why "Запиши" */}
          <div className={`rounded p-2 mb-3 text-[11px] ${isDark ? "bg-violet-500/10 text-violet-100" : "bg-violet-50 text-violet-900"}`}
            data-testid="mbi-explainer">
            <div className="font-semibold mb-1">
              {bg ? "ℹ️ Какво е това?" : lang === "es" ? "ℹ️ ¿Qué es esto?" : "ℹ️ What is this?"}
            </div>
            <div>
              {bg && (<>
                RCT-доказани психосоматични интервенции (медитация, йога, дишане, КПТ),
                които модулират <b>HPA ос → NF-κB → възпаление</b> — същите пътища,
                които и диетата targets. Бутонът <b>„Запиши“</b> записва проведена сесия
                (мин.) → сумира се към дневния Pathway Coverage заедно с храните →
                показва се в <i>PathwayCoverageCard</i> като „+ N пътища от MBI“.
                Всяка интервенция има <b>phenotype-specific механизъм</b> (разгънете „детайли“).
              </>)}
              {lang === "en" && (<>
                RCT-backed psychosomatic interventions (meditation, yoga, breathwork, CBT)
                that modulate the <b>HPA axis → NF-κB → inflammation</b> — the same pathways
                diet targets. The <b>“Log”</b> button records a practiced session (min) →
                aggregated with foods into your daily Pathway Coverage → shown in the
                <i> PathwayCoverageCard</i> as “+ N pathways from MBI”. Each intervention has
                a <b>phenotype-specific mechanism</b> (expand “details”).
              </>)}
              {lang === "es" && (<>
                Intervenciones psicosomáticas respaldadas por ECA (meditación, yoga,
                respiración, TCC) que modulan el <b>eje HPA → NF-κB → inflamación</b> —
                las mismas vías que la dieta. El botón <b>«Registrar»</b> registra una
                sesión practicada (min) → se suma a tu Pathway Coverage diario junto con
                los alimentos → se muestra en <i>PathwayCoverageCard</i> como «+ N vías
                desde MBI». Cada intervención tiene un <b>mecanismo específico por
                fenotipo</b> (amplíe «detalles»).
              </>)}
            </div>
          </div>

          {/* Weekly pathway coverage summary */}
          {weekly && weekly.pathway_count > 0 && (
            <div className={`rounded p-2 mb-3 flex items-center gap-2 text-[11px]
              ${isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}
              data-testid="mbi-weekly-coverage">
              <TrendingUp className="w-3.5 h-3.5" />
              <span className="flex-1">
                {bg && (<>
                  <b>{weekly.pathway_count}</b> KEGG пътя покрити тази седмица · {weekly.distinct_interventions?.length || 0} различни практики
                </>)}
                {lang === "en" && (<>
                  <b>{weekly.pathway_count}</b> KEGG pathways covered this week · {weekly.distinct_interventions?.length || 0} different practices
                </>)}
                {lang === "es" && (<>
                  <b>{weekly.pathway_count}</b> vías KEGG cubiertas esta semana · {weekly.distinct_interventions?.length || 0} prácticas distintas
                </>)}
              </span>
            </div>
          )}

          {loading && !recs && (
            <div className={`flex items-center gap-2 text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <Loader2 className="w-4 h-4 animate-spin" />
              {bg ? "Зареждам препоръки..." : lang === "es" ? "Cargando recomendaciones..." : "Loading recommendations..."}
            </div>
          )}

          <div className="space-y-2" data-testid="mbi-recommendations-list">
            {visible.map((mbi) => (
              <InterventionCard
                key={mbi.id}
                mbi={mbi}
                isDark={isDark}
                onLog={(m) => setLogModal({ open: true, intervention: m })}
                onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
                expanded={expandedId === mbi.id}
                lang={lang}
                bg={bg}
              />
            ))}
          </div>

          {recs.recommendations.length > 3 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className={`mt-3 w-full text-center text-[11px] font-semibold ${isDark ? "text-violet-300 hover:text-violet-200" : "text-violet-600 hover:text-violet-700"}`}
              data-testid="mbi-show-all-btn"
            >
              {showAll
                ? (bg ? "Покажи по-малко" : lang === "es" ? "Mostrar menos" : "Show less")
                : (bg ? `Покажи всички ${recs.recommendations.length}`
                   : lang === "es" ? `Mostrar todas las ${recs.recommendations.length}`
                   : `Show all ${recs.recommendations.length}`)}
            </button>
          )}
        </div>
      </div>

      <SessionLogModal
        open={logModal.open}
        onClose={() => setLogModal({ open: false, intervention: null })}
        intervention={logModal.intervention}
        API={API}
        isDark={isDark}
        onLogged={fetchData}
        lang={lang}
        bg={bg}
      />
    </>
  );
}
